import { Injectable } from '@angular/core';
import moment from 'moment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { BonusActivitCallBackData, GetBonusActivitParam } from '../interfaces/bank-card.interface';
import {
  ActivityDetail,
  ActivityList,
  BackwaterData,
  BackwaterListParams,
  BonusCustomerApply,
  BonusDetail,
  BonusDetailList,
  BonusDetailParams,
  BonusFlowParams,
  BonusGrantListData,
  BonusGrantParam,
  BonusSelect,
  BonusSortParam,
  BonusTypeList,
  ContestActivities,
  CouponCodeExchangeResult,
  CouponCodeParams,
  GrandType,
  GrandTypes,
  GrantTypeSelect,
  IExchangeInfo,
  MyRank,
  NoneActivated,
  NoneInActivatedCards,
  NoneStickyOverview,
  Rank,
  ReceiveBackwaterParams,
  RespActivityDetail,
  SeenType,
} from '../interfaces/bonus.interface';
import { ResponseListData } from '../interfaces/response.interface';
import { ResponseData } from './../interfaces/response.interface';
import { BaseApi } from './base.api';
@Injectable({
  providedIn: 'root',
})
export class BonusApi extends BaseApi {
  private get bonusUrl(): string {
    return `${environment.apiUrl}/v1/asset/bonus`;
  }
  // 新的累積救援金跟新的卡券使用的api
  // getBonusDetail
  // getLongTermBonusDetails
  // getReceiveBackwaters
  private get bonusUrlV2(): string {
    return `${environment.apiUrl}/v2/asset/bonus`;
  }

  private get noneStickyUrl(): string {
    return `${environment.apiUrl}/v1/asset/nonsticky`;
  }

  /**
   * 卡券发放方式，下拉列表
   */
  getGrantTypeSelect(): Observable<ResponseData<GrantTypeSelect[]>> {
    return this.get(`${this.bonusUrl}/getgranttypeselect`);
  }

  /**
   * 红利历史记录
   * @param params
   */
  getBonusGrantList(params: BonusGrantParam): Observable<ResponseListData<BonusGrantListData[]>> {
    return this.get(`${this.bonusUrl}/getbonusgrantlist`, params);
  }

  /**
   *  查询用户能参与的活动
   * @param currency 币种
   * @param level VIP等级
   * @param tenantId 商户id
   * @param uid UID
   * @param amount 金额
   * @param params
   * @returns
   */
  getBonusActivity(params: GetBonusActivitParam): Observable<Array<BonusActivitCallBackData> | null> {
    const url = `${environment.apiUrl}/v1/asset/bonus/getbonusactivity`;
    return this.get<ResponseData<Array<BonusActivitCallBackData>>>(url, params).pipe(map(x => x?.data || null));
  }

  /**
   * payment iq 传活动值
   * @param params
   * @param params.activityNo
   * @returns
   */
  onPiqActivityNo(params: { activityNo: string }): Observable<boolean> {
    const url = `${environment.apiUrl}/v1/asset/bonus/paymentiqdepositbonus`;
    return this.post<ResponseData<boolean>>(url, params).pipe(map(x => x?.data));
  }

  /**
   *  虚拟动值
   * @param params
   * @param params.activityNo
   * @param params.currency
   * @returns
   */
  onCryptoActivityNo(params: { activityNo: string; currency?: string }): Observable<boolean> {
    const url = `${environment.apiUrl}/v1/asset/bonus/cryptodepositbonus`;
    return this.post<ResponseData<boolean>>(url, params).pipe(map(x => x?.data));
  }

  /**
   * 获取卡券状态，下拉列表
   */
  getBonusSelect(): Observable<ResponseData<BonusSelect[]>> {
    return this.get(`${this.bonusUrl}/getbonusselect`);
  }

  /**
   * 获取卡券类型，下拉列表
   * @returns 卡券类型下拉数据
   */
  getBonusTypeList(): Observable<BonusTypeList[]> {
    return this.get<ResponseData<BonusTypeList[]>>(`${this.bonusUrl}/getbonustypelist`).pipe(
      map(x => {
        if (x?.data) {
          const data = x.data;
          let newTypeCode: string;
          const list = data.map((item: BonusTypeList) => {
            if (item.typeCode === null) {
              if (item.grantType === GrandType.Cash) {
                newTypeCode = 'xjj';
              }
              if (item.grantType === GrandType.SVIP) {
                newTypeCode = 'yhq';
              }
            } else {
              newTypeCode = item.typeCode;
            }
            return {
              ...item,
              title: `${item.title} ( ${item.total} )`,
              newTypeCode: newTypeCode,
            };
          });
          return list;
        } else {
          return [];
        }
      }),
    );
  }

  /**
   * 获取用户卡券列表
   * @param param
   * @param wdFeeType 手續費A或C
   * @returns
   */
  getBonusDetail(param: BonusDetailParams, wdFeeType: 'A' | 'C' = 'C'): Observable<BonusDetail> {
    return this.get<ResponseData<BonusDetail>>(`${this.bonusUrl}/bonusdetail`, param).pipe(
      map(v => {
        if (v?.data) {
          const data = v.data;
          let currency: string;
          let amount: string | number;
          let bottomText: string = '';
          const list = data.list.map((item: BonusDetailList) => {
            if (item.currency === 'Unknown') {
              currency = 'SVIP';
              amount = this.localeService.getValue('svip_coup');
            } else {
              currency = item.currency;
              amount = item.amount;
            }

            switch (item?.grantType) {
              case 'Cash':
                bottomText =
                  wdFeeType === 'A'
                    ? `${this.localeService.getValue('wd_limit')} ${
                        item?.withdrawFlowMultiple || 0
                      } ${this.localeService.getValue('times')}`
                    : `Withdrawal Wagering Requirements ${
                        item?.withdrawFlowMultiple || 0
                      } ${this.localeService.getValue('times')}`;
                break;
              case 'Coupon':
                if (item?.minBetLimit === 0) {
                  bottomText = `${this.localeService.getValue('single_credit')} ${
                    item?.maxBetRate || 0
                  }%, ${this.localeService.getValue('max_de')} ${item?.maxBetAmount || 0} USDT`;
                } else {
                  bottomText = `${this.localeService.getValue('single_credit')} ${
                    item?.maxBetRate || 0
                  }%, ${this.localeService.getValue('max_de')} ${
                    item?.maxBetAmount || 0
                  }USDT, ${this.localeService.getValue('min_bet_limit')} ${item?.minBetLimit || 10} USDT`;
                }
                break;
              default:
                bottomText = '';
                break;
            }

            return {
              ...item,
              currency,
              amount,
              bottomText,
              labels: [
                `${item?.activityType === 'Vip' ? this.localeService.getValue('vip_exclusive') : ''}`,
                ...(item?.labels || []),
                this.grandTypeTrans(item?.grantType || ''),
              ],
            };
          });

          return {
            ...data,
            list,
          };
        } else {
          return {
            list: [],
            total: 0,
          };
        }
      }),
    );
  }

  getBonusDetailV2(param: BonusDetailParams, wdFeeType: 'A' | 'C' = 'C'): Observable<BonusDetail> {
    return this.get<ResponseData<BonusDetail>>(`${this.bonusUrlV2}/bonusdetail`, param).pipe(
      map(v => {
        if (v?.data) {
          const data = v.data;
          let currency: string;
          let amount: string | number;
          let bottomText: string = '';
          const list = data.list.map((item: BonusDetailList) => {
            if (item.currency === 'Unknown') {
              currency = 'SVIP';
              amount = this.localeService.getValue('svip_coup');
            } else {
              currency = item.currency;
              amount = item.amount;
            }

            switch (item?.grantType) {
              case 'Cash':
                bottomText =
                  wdFeeType === 'A'
                    ? `${this.localeService.getValue('wd_limit')} ${
                        item?.withdrawFlowMultiple || 0
                      } ${this.localeService.getValue('times')}`
                    : `Withdrawal Wagering Requirements ${
                        item?.withdrawFlowMultiple || 0
                      } ${this.localeService.getValue('times')}`;
                break;
              case 'Coupon':
                if (item?.minBetLimit === 0) {
                  bottomText = `${this.localeService.getValue('single_credit')} ${
                    item?.maxBetRate || 0
                  }%, ${this.localeService.getValue('max_de')} ${item?.maxBetAmount || 0} USDT`;
                } else {
                  bottomText = `${this.localeService.getValue('single_credit')} ${
                    item?.maxBetRate || 0
                  }%, ${this.localeService.getValue('max_de')} ${
                    item?.maxBetAmount || 0
                  }USDT, ${this.localeService.getValue('min_bet_limit')} ${item?.minBetLimit || 10} USDT`;
                }
                break;
              default:
                bottomText = '';
                break;
            }

            return {
              ...item,
              currency,
              amount,
              bottomText,
              labels: [
                `${item?.activityType === 'Vip' ? this.localeService.getValue('vip_exclusive') : ''}`,
                ...(item?.labels || []),
                this.grandTypeTrans(item?.grantType || ''),
              ],
            };
          });

          return {
            ...data,
            list,
          };
        } else {
          return {
            list: [],
            total: 0,
          };
        }
      }),
    );
  }

  /**
   * 将卡券 类型转为文案
   * @param grandType 卡券类型
   * @returns 文案
   */
  grandTypeTrans(grandType: GrandTypes): string {
    switch (grandType) {
      case 'Cash':
        return this.localeService.getValue('cash_coupons');
      case 'Coupon':
        return this.localeService.getValue('cre_vou');
      case 'SVIP':
        return `SVIP${this.localeService.getValue('svip_coup')}`;
      case 'InKind':
        return this.localeService.getValue('in_kind_coupons');
      case 'Equip':
        return this.localeService.getValue('equipment_coupons');
      case 'FreeSpin':
        return this.localeService.getValue('free_spin_coupons');
      default:
        return this.localeService.getValue('after_cash_coupons');
    }
  }

  /**
   * 领取卡券
   * @param param
   * @returns
   */
  getReceiveBackwater(param: ReceiveBackwaterParams): Observable<ResponseData<any>> {
    this.dataCollectionService.addPoint({ eventId: 30020 });
    return this.get(`${this.bonusUrlV2}/receivebackwater`, param);
  }

  /**
   * 领取多张卡券
   * @param param
   * @returns
   */
  getReceiveBackwaters(param: any): Observable<ResponseData<any>> {
    this.dataCollectionService.addPoint({ eventId: 30020 });
    return this.get(`${this.bonusUrlV2}/receivebackwaters`, param);
  }

  /**
   * 一键领取卡券
   * @returns boolean
   */
  onBatchReceiveBonus(): Observable<boolean | null> {
    return this.post<ResponseData<boolean>>(`${this.bonusUrl}/batchreceivebonus`).pipe(map(v => v?.data || null));
  }

  /**
   * 抵用金排序
   * @param params
   * @returns
   */
  onOrderCreditBets(params: BonusSortParam[]): Observable<boolean | null> {
    return this.post<ResponseData<boolean>>(`${this.bonusUrl}/bonussort`, { bonusSort: params }).pipe(
      map(v => v?.data || null),
    );
  }

  /**
   * 获取抵用金使用详情
   * @param param
   * @returns
   */
  getBonusFlow(param: BonusFlowParams): Observable<ResponseData<BackwaterData>> {
    return this.get(`${this.bonusUrl}/bonusflow`, param);
  }

  /**
   * 获取反水数据
   * @param param
   * @returns
   */
  getQueryBackwaterList(param: BackwaterListParams): Observable<ResponseData<BackwaterData>> {
    return this.get(`${this.bonusUrl}/querybackwaterlist`, param);
  }

  /**
   * 首页展示: 获取活动列表
   * @param params
   * @param params.equipment
   * @params equipment - Web, H5 , App
   * @returns []
   */
  getActivityInfo(params: { equipment: string }): Observable<ActivityList> {
    const url = `${this.bonusUrl}/getactivityinfo`;
    return this.get(url, params).pipe(
      map((x: any) => {
        if (x?.data) {
          const data = x.data;
          if (!data || !data?.list) return { list: [] };
          const sortActivities = (list: RespActivityDetail[] = []) =>
            [...list].sort((a, b) => Number(a?.sort || 0) - Number(b?.sort || 0));
          const activityGroups = data.list.map((item: ActivityList['list'][number]) => ({
            ...item,
            list: sortActivities(item.list),
          }));
          const allLists: RespActivityDetail[] = [];
          activityGroups.forEach((item: ActivityList['list'][number]) => {
            allLists.push(...item.list);
          });
          const addAllActivities = {
            labelCode: 'all',
            labelName: this.localeService.getValue('all'),
            list: sortActivities(allLists),
          };
          return { list: [addAllActivities, ...activityGroups] };
        }
        return { list: [] };
      }),
    );
  }

  /**
   * 活动详情
   * @param params 请求活动详情参数
   * @returns
   */
  getActivityDetail(params: ActivityDetail): Observable<RespActivityDetail | null> {
    const url = `${this.bonusUrl}/getactivitydetail`;
    return this.get(url, params).pipe(
      map((x: any) => {
        if (x?.data) {
          const data = x.data as RespActivityDetail;
          return {
            ...data,
            content: this.localeService.brandNameReplace(data.content ?? ''),
          };
        }
        return null;
      }),
    );
  }

  /**
   * 竞赛获取我的排名
   * @param params 活动编号
   * @param params.activitiesNo
   */
  getMyRankById(params: { activitiesNo: string }): Observable<MyRank> {
    const url = `${this.bonusUrl}/getrankbyid`;
    return this.get(url, params).pipe(
      map((x: any) => {
        if (x?.data) {
          const data = x.data;
          let period: string = '';
          const remainingTime = Number(data?.endTime || 0) - Number(data?.nowTime || 0);
          switch (data.period) {
            case 'day':
              period = `24 ${this.localeService.getValue('hour_unit')}`;
              break;
            case 'week':
              period = `7 ${this.localeService.getValue('day')}`;
              break;
            case 'month':
              period = `30 ${this.localeService.getValue('day')}`;
              break;
            case 'year':
              period = `365 ${this.localeService.getValue('day')}`;
              break;
            case 'single':
              period = this.localeService.getValue('single');
              break;
            default:
              break;
          }

          return {
            ...data,
            remainingTime,
            text: `${data.title}${period ? ' - ' + period : ''}`,
          };
        }

        return null;
      }),
    );
  }

  /** 获取竞赛活动 名称*/
  getContestTitleList(): Observable<ContestActivities> {
    const url = `${this.bonusUrl}/getcontestactivities`;
    return this.get(url).pipe(
      map((x: any) => {
        if (x?.data) {
          const data = x.data as ContestActivities;
          let period: string = '';
          let endTime: string = '';
          const dayStamp = 24 * 60 * 60 * 1000;
          const hourStamp = 60 * 60 * 1000;

          const onEndTime = (item: ContestActivities): number => {
            const timeGap = Number(item.endTime) - Number(item.nowTime);
            const unit: 'days' | 'minutes' | 'hours' =
              timeGap > dayStamp ? 'days' : timeGap > hourStamp ? 'hours' : 'minutes';
            return moment(item.endTime).diff(moment(item.nowTime), unit);
          };

          const onEndTimeUnit = (item: ContestActivities) => {
            const timeGap = Number(item.endTime).minus(item.nowTime);
            const unit = timeGap > dayStamp ? 'after_day' : timeGap > hourStamp ? 'after_hours' : 'after_minutes';
            return this.localeService.getValue(unit);
          };
          const title = data.title
            .map((item: any) => {
              const remainingTime = Number(item?.endTime || 0).minus(item?.nowTime || 0);
              switch (item.period) {
                case 'day':
                  period = `24 ${this.localeService.getValue('hour_unit')}`;
                  endTime = `${onEndTime(item)} ${onEndTimeUnit(item)}`;
                  break;
                case 'week':
                  period = `7 ${this.localeService.getValue('day')}`;
                  endTime = `${onEndTime(item)} ${onEndTimeUnit(item)}`;
                  break;
                case 'month':
                  period = `30 ${this.localeService.getValue('day')}`;
                  endTime = `${onEndTime(item)} ${onEndTimeUnit(item)}`;
                  break;
                case 'year':
                  period = `365 ${this.localeService.getValue('day')}`;
                  endTime = `${onEndTime(item)} ${onEndTimeUnit(item)}`;
                  break;
                case 'single':
                  period = this.localeService.getValue('single');
                  endTime = `${onEndTime(item)} ${onEndTimeUnit(item)}`;
                  break;
                default:
                  break;
              }
              return {
                ...item,
                endTime,
                icon: 'icon-race-clock',
                remainingTime,
                text: `${item.title}${period ? ' - ' + period : ''}`,
              };
            })
            .filter(item => item.title !== 'unknown');
          return {
            ...data,
            title,
          };
        }
        return { title: [] };
      }),
    );
  }

  /**
   * 获取竞赛所有排名
   * @param params 参数
   * @param params.activitiesNo
   * @param params.pageIndex
   * @param params.pageSize
   */
  getRank(params: { activitiesNo: string; pageIndex: number; pageSize: number }): Observable<Rank> {
    const url = `${this.bonusUrl}/getrank`;
    return this.get<ResponseData<Rank>>(url, params).pipe(
      map(x => {
        if (x?.data) {
          const data = x.data as Rank;
          if (data.bonusInfo) {
            const bonusInfo = data?.bonusInfo?.map(info => ({
              ...info,
              userName: info?.userName?.length > 0 ? info?.userName : this.localeService.getValue('invisible'),
              rankNumber: Number(info?.rankNumber || 0),
            }));

            return {
              ...data,
              bonusInfo,
            };
          }

          return data;
        }
        return { bonusInfo: [], total: 0, activitiesNo: '' };
      }),
    );
  }

  /**
   * 活动报名接口
   * @param params 申请参数
   * @returns
   */
  bonusCustomerApply(params: BonusCustomerApply): Observable<boolean> {
    const url = `${this.bonusUrl}/bonuscustomerapply`;
    return this.post(url, params).pipe(map((x: any) => x?.data || false));
  }

  /** 获取卡劵数量  */
  getBonusCount(): Observable<number> {
    const url = `${this.bonusUrl}/getbonuscount`;
    return this.get(url).pipe(map((x: any) => x?.data || 0));
  }

  /**
   * 券码兑换优惠券
   * @param params 优惠券号
   * @param params.exchangeCode
   * @returns
   */
  setExchangeReceive(params: { exchangeCode: string }): Observable<boolean | null> {
    const url = `${this.bonusUrl}/exchangereceive`;
    return this.post(url, params).pipe(map((x: any) => (x === null ? null : x?.data || false)));
  }

  /**
   * 优惠券查询记录
   * @param params
   * @param params.pageIndex
   * @param params.pageSize
   * @params 页码
   * @returns
   */
  getExchangeReceiveInfo(params: { pageIndex: number; pageSize: number }): Observable<IExchangeInfo> {
    const url = `${this.bonusUrl}/exchangereceiveinfo`;
    return this.get<ResponseData<IExchangeInfo>>(url, params).pipe(map(x => x?.data || { total: 0, list: [] }));
  }

  /**
   * 激活None Sticky Card
   * @param params
   * @param params.code 卡券id
   * @returns boolean
   */
  onActivateNoneStickyCard(params: { code: string }): Observable<boolean> {
    const url = `${this.noneStickyUrl}/activatewallet`;
    return this.post<ResponseData<boolean>>(url, params).pipe(map(v => v?.data));
  }

  /**
   * 放弃当前 None sticky card
   * @param params
   * @param params.code  卡券id
   * @returns boolean
   */
  onDiscardNoneStickyCard(params: { code: string }): Observable<boolean> {
    const url = `${this.noneStickyUrl}/cancelwallet`;
    return this.post<ResponseData<boolean>>(url, params).pipe(map(v => v?.data));
  }

  /**
   * 提现当前卡券到钱包
   * @param params
   * @param params.code 卡券id
   * @returns boolean
   */
  onWidthdrawNoneStickyCard(params: { code: string }): Observable<boolean> {
    const url = `${this.noneStickyUrl}/withdrawwallet`;
    return this.post<ResponseData<boolean>>(url, params).pipe(map(v => v?.data));
  }

  /**
   * 获取 非粘性卡券总览
   * @returns
   */
  getNoneStickyOverview(): Observable<NoneStickyOverview | null> {
    const url = `${this.noneStickyUrl}/walletoverview`;
    return this.get<ResponseData<NoneStickyOverview>>(url).pipe(map(v => v?.data || null));
  }

  /**
   * 获取已经激活非粘性卡券
   * @returns
   */
  getActivatedNoneSticky(): Observable<NoneActivated | null> {
    const url = `${this.noneStickyUrl}/getactivated`;
    return this.post<ResponseData<NoneActivated>>(url).pipe(map(v => v?.data || null));
  }

  /**
   * 卡券文案
   * @param params
   * @param params.code
   * @param params.category
   * @param params.isDeposit
   * @returns
   */
  getNoneStickyDetail(params: {
    code: string;
    isDeposit: boolean;
    category: string;
  }): Observable<{ content: string } | null> {
    const url = `${this.noneStickyUrl}/getdetail`;
    return this.post<ResponseData<{ content: string }>>(url, params).pipe(map(v => v?.data || null));
  }

  getNoneInActivatedCards(): Observable<NoneInActivatedCards | null> {
    const url = `${this.noneStickyUrl}/getlist`;
    return this.post<ResponseData<NoneInActivatedCards>>(url).pipe(map(v => v?.data || null));
  }

  /**
   * 一件领取所有卡券
   * @returns boolean
   */
  onWithdrawalAllCards(): Observable<boolean> {
    const url = `${this.noneStickyUrl}/batchwithdrawwallet`;
    return this.post<ResponseData<boolean>>(url).pipe(map(v => v?.data));
  }

  /**
   * 红利弹窗 兑换码
   * @param params
   * @returns
   */
  getCoupondeposit(params: CouponCodeParams): Observable<ResponseData<CouponCodeExchangeResult>> {
    const url = `${this.bonusUrl}/getcoupondeposit`;
    return this.get(url, params);
  }

  /**
   * 獲取從創帳號到目前以獲取的紅利總額
   * @returns
   */
  getTotalCompletedBonusAndNewBonusAmount(): Observable<ResponseData<any>> {
    const url = `${this.bonusUrl}/gettotalcompletedbonusandnewbonusamount`;
    return this.get(url);
  }

  /**
   * 获取长期红利详情
   * @returns 长期红利详情数据
   */
  getLongTermBonusDetails(): Observable<any> {
    const url = `${this.bonusUrlV2}/longtermbonusdetail`;
    return this.get<ResponseListData<any>>(url).pipe(map(x => x?.data?.list || null));
  }

  /**
   * 获取活动数量
   * @param params
   * @param params.equipment 设备类型
   * @returns 活动数量
   */
  getActivityCount(params: { equipment: string }): Observable<number> {
    const url = `${this.bonusUrl}/getactivitycount`;
    return this.get(url, params).pipe(map((x: any) => x?.data?.activityCount || 0));
  }

  /**
   * 讀取BonusCount
   * @param params
   * @param params.seenType 類型 1. BonusUnclaimed 2. Activity
   * @returns 讀取結果
   */
  seenBonusCount(params: { seenType: SeenType }): Observable<number> {
    const url = `${this.bonusUrl}/seenbonuscount`;
    return this.get(url, params).pipe(map((x: any) => x?.data || 0));
  }
}
